import { useRef, useState } from 'react';
import { accounts } from '../backend';
import { useApp } from '../store';
import { signInTarget, targetIsElsewhere } from '../invite';
import { allStepsFor, scopesAnswered, stepsFor, type StepId } from '../setup';
import { PROJECT_ROLES, ROLE_LABEL, ROLE_BLURB, can, type ProjectRole } from '../permissions';
import { SOURCE_BLURB, SOURCE_LABEL, SOURCES, DEFAULT_SOURCE_NAME } from '../ownership';
import { SUGGESTED_CHECKS } from '../done';
import { assistantsOf } from '../assistant';
import { useDismiss, useEscapeToClose, useFocusTrap } from '../motion';
import Icon from './Icon';

/** A person being added, before anything is written. */
interface Draft {
  email: string;
  name: string;
  role: ProjectRole;
}

const BLANK: Draft = { email: '', name: '', role: 'reviewer' };

/**
 * The questions asked once, when there is nobody to ask them of you.
 *
 * It opens by itself the first time you use the app, and again on a project
 * nobody has set up — those are the only two moments when the answers are
 * missing and somebody is present to give them.
 *
 * EVERY ANSWER IS WRITTEN AS IT IS GIVEN.
 *
 * Not gathered up and saved at the end. Somebody who closes this halfway keeps
 * what they answered, which is the behaviour the settings screen already has,
 * and it means abandoning the wizard is never worse than not starting it.
 *
 * The exception is the team list, which is written when you leave that step —
 * a half-typed address is not a person, and adding one on every keystroke would
 * put four broken entries on the board while somebody types one good one.
 */
export default function SetupWizard() {
  const meta = useApp((s) => s.meta);
  const role = useApp((s) => s.shownRole);
  const profiles = useApp((s) => s.profiles);
  const access = useApp((s) => s.access);
  const myEmail = (useApp((s) => s.user?.email) ?? '').toLowerCase();
  const setMeta = useApp((s) => s.setMeta);
  const setPersonProfile = useApp((s) => s.setPersonProfile);
  const addPerson = useApp((s) => s.addPerson);
  const dismissSetup = useApp((s) => s.dismissSetup);
  // Leaves the same way the issue card does — see src/motion.ts.
  const { leaving, dismiss } = useDismiss(dismissSetup);
  const panelRef = useRef<HTMLDivElement>(null);
  useEscapeToClose(dismiss);
  useFocusTrap(panelRef);

  const me = profiles[myEmail];
  const canSettings = can(role, 'editSettings');
  const all = useApp((s) => s.setupAll);
  // From Settings, every question is on the table again. From the board's
  // offer, only what is outstanding — re-asking what somebody has already
  // answered is padding out the thing they were just persuaded to start.
  const steps = all
    ? allStepsFor(canSettings)
    : stepsFor({ canSettings, meta, memberCount: access.emails.length, profile: me });
  const [at, setAt] = useState(0);
  const [team, setTeam] = useState<Draft[]>([{ ...BLANK }]);
  const [checks, setChecks] = useState((meta.doneChecks ?? []).join('\n'));
  // What went wrong adding somebody, in their own words. Held rather than
  // thrown: a failure here must not be able to stop the rest of setup.
  const [trouble, setTrouble] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  // On by default. Somebody added to a board and never told cannot use it, and
  // the first person to try this expected the email and did not get one — which
  // is a sound instinct about what "add your team" ought to mean.
  const [sendLinks, setSendLinks] = useState(true);

  if (steps.length === 0) return null;
  const step = steps[Math.min(at, steps.length - 1)];
  const last = at >= steps.length - 1;

  /**
   * Writes the people typed on the team step. Blank rows are simply not people.
   *
   * EVERY FAILURE IS REPORTED AND NONE OF THEM STOPS ANYTHING.
   *
   * This threw, the rejection was swallowed, and the Next button did nothing at
   * all — no message, no movement, on the one step where it is most likely to
   * fail. Adding somebody writes to the access list, which is the most guarded
   * document on the board, so refusal is a NORMAL outcome here rather than an
   * exceptional one.
   *
   * @returns the ones that could not be added, said plainly.
   */
  const saveTeam = async (): Promise<string[]> => {
    const failed: string[] = [];
    for (const p of team) {
      const email = p.email.trim().toLowerCase();
      if (!email.includes('@') || access.emails.includes(email)) continue;
      try {
        await addPerson(email, p.role);
        // Their name, given while you were already typing their address. Without
        // this the board shows an email on every comment they ever write.
        if (p.name.trim()) await setPersonProfile(email, { name: p.name.trim() });
        if (sendLinks) {
          // Firebase sends this itself. Reported apart from the add: somebody
          // who is on the board but whose email bounced is a different problem
          // from somebody who was never added, and telling you they "were not
          // added" when they were would send you looking in the wrong place.
          try {
            await accounts.sendSignInLink(email, signInTarget());
          } catch (e) {
            const code = (e as { code?: string }).code ?? 'it did not say why';
            failed.push(`${email} was added, but the sign-in link could not be sent — ${code}.`);
          }
        }
      } catch (e) {
        const why = e instanceof Error ? e.message : String(e);
        failed.push(
          /permission/i.test(why)
            ? `${email} — you are not allowed to add people to this board.`
            : `${email} — ${why}`
        );
      }
    }
    return failed;
  };

  const close = () => {
    const answered = scopesAnswered(steps);
    const now = Date.now();
    if (answered.project) setMeta({ setUpAt: now });
    if (answered.person && myEmail) void setPersonProfile(myEmail, { setUpAt: now });
    dismiss();
  };

  const next = async () => {
    setBusy(true);
    try {
      if (step.id === 'team') {
        const failed = await saveTeam();
        setTrouble(failed);
        // Stay on the step so the message is next to the rows it is about — but
        // only once. Pressing Next again moves on regardless, because one bad
        // address must not be able to trap somebody in the middle of setup.
        if (failed.length > 0 && trouble.length === 0) return;
      }
      if (last) close();
      else setAt(at + 1);
    } finally {
      setBusy(false);
    }
  };

  const bots = assistantsOf(meta);

  return (
    <div
      className={`overlay setup-overlay ${leaving ? 'is-leaving' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label="Set up"
    >
      <div ref={panelRef} tabIndex={-1} className={`setup ${leaving ? 'is-leaving' : ''}`}>
        <div className="setup-top">
          <span className="setup-count">
            Question {at + 1} of {steps.length}
          </span>
          <button className="btn btn-small" onClick={close}>
            Skip the rest
          </button>
        </div>
        <div className="setup-bar" aria-hidden="true">
          <span style={{ width: `${((at + 1) / steps.length) * 100}%` }} />
        </div>

        <h2>{step.title}</h2>
        <div className="setup-body">{body(step.id)}</div>

        {trouble.length > 0 && (
          <div className="setup-trouble" role="alert">
            <strong>
              {trouble.length === 1 ? 'That person was not added' : 'Those people were not added'}
            </strong>
            {trouble.map((t) => (
              <span key={t}>{t}</span>
            ))}
            <span className="hint">
              Everything else you have answered is saved. Press {last ? 'Finish' : 'Next'} again to
              carry on without them — they can be added later under People.
            </span>
          </div>
        )}

        <div className="setup-foot">
          {at > 0 && (
            <button className="btn" onClick={() => setAt(at - 1)}>
              Back
            </button>
          )}
          <span className="setup-spacer" />
          <button className="btn btn-primary" disabled={busy} onClick={() => void next()}>
            {busy ? 'Saving…' : last ? 'Finish' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );

  function body(id: StepId) {
    switch (id) {
      case 'name':
        return (
          <>
            <label className="stack">
              Project name
              <input
                className="input"
                autoFocus
                value={meta.name}
                onChange={(e) => setMeta({ name: e.target.value })}
              />
            </label>
            <label className="stack">
              A short key <span className="hint">(goes in front of every issue number, e.g. ORB)</span>
              <input
                className="input key-input"
                value={meta.key}
                onChange={(e) => setMeta({ key: e.target.value.toUpperCase().slice(0, 5) })}
              />
            </label>
          </>
        );

      case 'source':
        return (
          <>
            <p className="hint">
              This decides what the board may edit. It describes how a team works, so it rarely
              changes afterwards.
            </p>
            <div className="mode-cards">
              {SOURCES.map((src) => (
                <button
                  key={src}
                  className={`mode-card ${(meta.source ?? 'tracker') === src ? 'is-on' : ''}`}
                  onClick={() => setMeta({ source: src })}
                >
                  <span className="mode-card-title">{SOURCE_LABEL[src]}</span>
                  <span className="mode-card-blurb">{SOURCE_BLURB[src]}</span>
                </button>
              ))}
            </div>
            {(meta.source ?? 'tracker') === 'tracker' && (
              <label className="stack">
                What is it called?{' '}
                <span className="hint">(a file, a system, a script — whatever it is)</span>
                <input
                  className="input"
                  value={meta.sourceName ?? ''}
                  placeholder={DEFAULT_SOURCE_NAME}
                  onChange={(e) => setMeta({ sourceName: e.target.value })}
                />
              </label>
            )}
          </>
        );

      case 'repo':
        return (
          <>
            <p className="hint">
              When an issue records a commit, this turns it into a link. Leave it empty if there is
              nothing to link to.
            </p>
            <label className="stack">
              Address of the repository
              <input
                className="input"
                autoFocus
                value={meta.repoUrl ?? ''}
                placeholder="https://github.com/owner/repo"
                onChange={(e) => setMeta({ repoUrl: e.target.value.trim().replace(/\/+$/, '') })}
              />
            </label>
          </>
        );

      case 'done':
        return (
          <>
            <p className="hint">
              Nothing can be moved to Done until every one of these is ticked. One list for the whole
              project, so &ldquo;done&rdquo; means the same thing on every issue.
            </p>
            <label className="stack">
              One per line
              <textarea
                className="input desc"
                rows={4}
                value={checks}
                placeholder={SUGGESTED_CHECKS.join('\n')}
                onChange={(e) => {
                  setChecks(e.target.value);
                  setMeta({ doneChecks: e.target.value.split('\n').map((l) => l.trim()).filter(Boolean) });
                }}
              />
            </label>
            {checks.trim() === '' && (
              <button
                className="btn btn-small"
                onClick={() => {
                  setChecks(SUGGESTED_CHECKS.join('\n'));
                  setMeta({ doneChecks: SUGGESTED_CHECKS });
                }}
              >
                Use a sensible starting list
              </button>
            )}
          </>
        );

      case 'you':
        return (
          <>
            <p className="hint">
              This is what the board calls you — on comments, on issues you are assigned, everywhere.
              Without it, everyone sees your email address instead.
            </p>
            <label className="stack">
              Your name
              <input
                className="input"
                autoFocus
                value={me?.name ?? ''}
                placeholder="Pat Moore"
                onChange={(e) => void setPersonProfile(myEmail, { name: e.target.value })}
              />
            </label>
            <label className="stack">
              What you do here
              <input
                className="input"
                value={me?.title ?? ''}
                placeholder="Designer, or advising on the API"
                onChange={(e) => void setPersonProfile(myEmail, { title: e.target.value })}
              />
            </label>
          </>
        );

      case 'team':
        return (
          <>
            <p className="hint">
              Adding somebody gives them access. Leave the box below ticked and they also get an
              email with a one-click sign-in link — no password to make up. You can add more at any
              time under <em>People</em>.
            </p>
            <div className="setup-team">
              {team.map((p, n) => (
                <div className="setup-person" key={n}>
                  <input
                    className="input"
                    value={p.email}
                    placeholder="their@email.com"
                    onChange={(e) => edit(n, { email: e.target.value })}
                  />
                  <input
                    className="input"
                    value={p.name}
                    placeholder="Their name"
                    onChange={(e) => edit(n, { name: e.target.value })}
                  />
                  <select
                    className="input"
                    value={p.role}
                    onChange={(e) => edit(n, { role: e.target.value as ProjectRole })}
                  >
                    {PROJECT_ROLES.map((r) => (
                      <option key={r} value={r}>
                        {ROLE_LABEL[r]}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
            <p className="hint">{ROLE_BLURB[team[team.length - 1]?.role ?? 'reviewer']}</p>
            <button className="btn btn-small" onClick={() => setTeam([...team, { ...BLANK }])}>
              <Icon name="plus" size={13} /> Add another
            </button>
            <label className="switch-row">
              <input
                type="checkbox"
                checked={sendLinks}
                onChange={(e) => setSendLinks(e.target.checked)}
              />
              <span>
                Email each of them a sign-in link
                <span className="hint">
                  {targetIsElsewhere()
                    ? ' — the link takes them to the live board, not this test copy'
                    : ' — untick if you would rather tell them yourself'}
                </span>
              </span>
            </label>
          </>
        );

      case 'assistants':
        return bots.length > 0 ? (
          <>
            <p className="hint">
              {bots.length === 1
                ? `${bots[0].name?.trim() || bots[0].id} has written to this board.`
                : `${bots.length} assistants have written to this board.`}{' '}
              They can be allowed to read comments and answer them on the card. Anything that would
              CHANGE something still waits for a person to say yes.
            </p>
            <label className="switch-row">
              <input
                type="checkbox"
                checked={!!meta.assistantMayAnswer}
                onChange={(e) => setMeta({ assistantMayAnswer: e.target.checked })}
              />
              <span>Let an assistant read and answer comments here</span>
            </label>
            <p className="hint">
              Yours to withdraw at any time, under Settings → Assistants, where each one has its own
              switch.
            </p>
          </>
        ) : (
          <p className="hint">
            None has been seen on this board yet. An assistant appears here by actually writing to
            the board, not by being added — so there is nothing to set until one does. When that
            happens, Settings → Assistants is where you decide what it may do.
          </p>
        );

      case 'notify':
        return (
          <>
            <p className="hint">
              Notifications appear while the board is open in a browser tab. Nothing is emailed.
            </p>
            <div className="mode-cards">
              {(
                [
                  ['all', 'Every comment', 'Anything anybody writes, on any issue.'],
                  ['mentions', 'Only when I am named', 'Just comments with your name in them.'],
                  ['none', 'Nothing', 'Never interrupt me. The board still shows everything.'],
                ] as const
              ).map(([value, label, blurb]) => (
                <button
                  key={value}
                  className={`mode-card ${(me?.notifyWhen ?? 'all') === value ? 'is-on' : ''}`}
                  onClick={() => void setPersonProfile(myEmail, { notifyWhen: value })}
                >
                  <span className="mode-card-title">{label}</span>
                  <span className="mode-card-blurb">{blurb}</span>
                </button>
              ))}
            </div>
          </>
        );
    }
  }

  function edit(n: number, patch: Partial<Draft>) {
    setTeam(team.map((p, i) => (i === n ? { ...p, ...patch } : p)));
  }
}
